import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { OnlineStatusService } from 'ngx-online-status';
import {
  BehaviorSubject,
  debounceTime,
  fromEvent,
  Observable,
  Subscription,
} from 'rxjs';
import { EventService } from 'src/app/services/event.service';
import { LoadingService } from 'src/app/services/loading.service';
import { MetaService } from 'src/app/services/meta.service';
import { PropertyService } from 'src/app/services/property.service';

@Component({
  selector: 'app-properties',
  templateUrl: './properties.component.html',
  styleUrls: ['./properties.component.css'],
})
export class PropertiesComponent implements OnInit, OnDestroy {
  isMobile: BehaviorSubject<boolean> = new BehaviorSubject(true);
  networkStatus: any = 1;
  desktopProperties!: any[];
  mobileProperties!: any[];
  totalProperties!: number;
  highlightsProperties!: any[];
  searchData!: any;
  pageTitle: string = 't-properties';
  page: number = 1;
  filters: any = {};
  resizeObservable$!: Observable<Event>;
  resizeSubscription$!: Subscription;
  checkedCharacteristics: any[] = [];
  lastSearch: { isLastSearch: any; value: any } = {
    isLastSearch: false,
    value: '',
  };
  orderValue: any = { by: 'createdAt', dir: 'desc' };
  pushProperties: boolean = true;
  isNearby: boolean = false;
  subscriptions: Subscription = new Subscription();
  requests: number = 0;
  mapProperties: boolean = false;

  constructor(
    private eventService: EventService,
    private route: ActivatedRoute,
    private loadingService: LoadingService,
    private dbService: NgxIndexedDBService,
    private propertyService: PropertyService,
    private onlineService: OnlineStatusService,
    private metaService: MetaService
  ) { }

  ngOnInit(): void {
    this.metaService.changeMeta('PROPERTIES', this.pageTitle);
    this.loadingService.setLoading(true);
    this.loadCss();
    /**
     * Send Page to Header
     */
    this.eventService.broadcast('header-page-title', this.pageTitle);

    /**
     * Subscribe and init resize Event
     */
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = this.resizeObservable$
      .pipe(debounceTime(100))
      .subscribe((evt: any) => {
        this.onResize(evt.target);
      });
    this.onResize();
    this.networkStatus = this.onlineService.getStatus();
    this.onlineService.status.subscribe(
      (status) => (this.networkStatus = status)
    );

    this.route.queryParamMap.subscribe((params) => {
      if (params.keys.length > 0) {
        this.page = 1;
        const searchKeys = [
          'reference',
          'freeSearch',
          'location',
          'characteristics',
          'localAccommodation'
        ];
        if (this.filters.freeSearch) {
          this.filters = {
            freeSearch: this.filters.freeSearch,
          };
        }
        if (sessionStorage.getItem('agent-search')) {
          this.filters.agent = sessionStorage.getItem('agent-search');
          sessionStorage.removeItem('agent-search');
        }
        // this.filters = {};
        this.filters.order = this.orderValue;
        this.checkedCharacteristics = [];
        delete this.filters?.characteristics;
        delete this.filters?.reference;
        delete this.filters?.bedrooms;
        delete this.filters?.bathrooms;
        if (params.keys.includes('r')) {
          delete this.filters?.businessType;
          delete this.filters?.propertyType;
          delete this.filters?.propertyCondition;
          delete this.filters?.priceMin;
          delete this.filters?.priceMax;
          delete this.filters?.areaMin;
          delete this.filters?.areaMax;
          delete this.filters?.bedrooms;
          delete this.filters?.bathrooms;
          delete this.filters?.agent;
        }
        params.keys.map((key) => {
          this.mobileProperties = [];
          if (searchKeys.some((k) => key.includes(k))) {
            if (key === 'characteristics') {
              const data: any = params.get(key);
              this.checkedCharacteristics = JSON.parse(data);
            }
            if (key === 'freeSearch') {
              this.sendToHeader(params.get(key));
            }
            this.filters[key] = params.get(key);
          } else {
            if (key == 'lastSearch') {
              this.lastSearch = {
                isLastSearch: params.get(key),
                value: params.get('freeSearch'),
              };
            } else if (key == 'page') {
              this.pushProperties = true;
              const p = Number(params.get(key));
              this.page = (p - 1) * 8;
            } else if (key == 'latlng') {
              this.isNearby = true;
              if (localStorage.getItem('latlng')) {
                this.filters['nearby'] = localStorage.getItem('latlng');
              }
            } else {
              this.filters[key] = Number(params.get(key));
            }
          }
        });
        if (this.requests === 0) {
          this.initProperties();
          this.requests++;
        } else {
          this.getProperties(this.filters);
        }
      } else {
        this.filters = {};
        this.filters.order = this.orderValue;
        if (sessionStorage.getItem('agent-search')) {
          this.filters.agent = sessionStorage.getItem('agent-search');
          sessionStorage.removeItem('agent-search');
        }
        if (this.requests === 0) {
          this.initProperties();
          this.requests++;
        } else {
          this.getProperties(this.filters);
        }
      }
    });
    /**
     * Subscribe event if refresh is necessary
     */
    const eventRefresh = this.eventService.subscribe('refresh-properties', (page) => {
      this.pushProperties = true;
      this.page = page;
      this.getProperties(this.filters);
    });
    const langSubscription = this.eventService.subscribe(
      'language-change',
      () => {
        this.getProperties(this.filters);
      }
    );
    const eventOrder = this.eventService.subscribe('order-properties', (order) => {
      this.filters['order'] = order;
      this.pushProperties = false;
      this.orderValue = order;
      if (!this.isMobile.value) {
        this.getProperties(this.filters);
      }
    });

    const eventSearch = this.eventService.subscribe('header-search-properties', (search) => {
      if (search != '') {
        this.filters['freeSearch'] = search;
        this.page = 1;
        this.mobileProperties = [];
        this.getProperties(this.filters, false);
      } else {
        delete this.filters['freeSearch'];
        this.page = 1;
        this.mobileProperties = [];
        this.getProperties(this.filters);
      }
    });

    const eventLocation = this.eventService.subscribe('new-location', () =>
      this.calcNearbyProperties()
    );

    const eventClear = this.eventService.subscribe('clear-filter', (filter) => {
      if (this.filters[filter]) {
        delete this.filters[filter];
      }
    });
    this.subscriptions.add(langSubscription);
    this.subscriptions.add(eventClear);
    this.subscriptions.add(eventLocation);
    this.subscriptions.add(eventSearch);
    this.subscriptions.add(eventOrder);
    this.subscriptions.add(eventRefresh);
  }

  sendToHeader(value: any) {
    this.eventService.broadcast('search-to-header', value);
  }

  initProperties() {
    this.getDataToSearch();
    this.getProperties(this.filters);
    this.getFeaturedProperties();
    this.loadingService.setLoading(false);
  }

  getProperties(filters: any, include: boolean = true) {
    const propertySubscription = this.propertyService
      .getProperties(filters, this.page)
      .subscribe({
        next: (data) => {
          this.mapProperties = data.mapProperties;
          this.totalProperties = data.total;
          if (this.totalProperties > 0) {
            data.properties.forEach((property) => {
              this.dbService.update('searched-properties', property).subscribe({
                error: (err: string) => {
                  if (err.startsWith('objectStore does not exists')) {
                    this.dbService.createObjectStore({
                      store: 'searched-properties',
                      storeConfig: {
                        keyPath: 'uniqueId',
                        autoIncrement: false,
                      },
                      storeSchema: [],
                    });
                  }
                },
              });
              if (property?.highlight) {
                this.dbService
                  .update('highlights-properties', property)
                  .subscribe({
                    error: (err: string) => {
                      if (err.startsWith('objectStore does not exists')) {
                        this.dbService.createObjectStore({
                          store: 'highlights-properties',
                          storeConfig: {
                            keyPath: 'uniqueId',
                            autoIncrement: false,
                          },
                          storeSchema: [],
                        });
                      }
                    },
                  });
              }
            });
          }
          if (this.isMobile.value && this.mobileProperties?.length > 0) {
            if (this.pushProperties) {
              data.properties.forEach((property) =>
                this.mobileProperties.push(property)
              );
            } else {
              this.pushProperties = true;
              this.mobileProperties = data.properties;
            }
          } else {
            this.desktopProperties = data.properties;
            this.mobileProperties = data.properties;
          }
          if (this.filters.freeSearch && include) {
            this.dbService
              .update('latest-searches', {
                searchKey: this.filters.freeSearch,
                results: this.mobileProperties,
                total: this.totalProperties,
                type: 'freeSearch',
              })
              .subscribe({
                next: () => {
                  this.eventService.broadcast('latest-searches-update');
                },
                error: (err: string) => {
                  if (err.startsWith('objectStore does not exists')) {
                    this.dbService.createObjectStore({
                      store: 'latest-searches',
                      storeConfig: {
                        keyPath: 'searchKey',
                        autoIncrement: false,
                      },
                      storeSchema: [],
                    });
                  }
                },
              });
          }
          if (this.isNearby) {
            this.calcNearbyProperties();
          }
          if (this.filters.location) {
            this.dbService
              .getByKey('latest-searches', this.filters.location)
              .subscribe((search: any) => {
                if (search) {
                  this.dbService
                    .update('latest-searches', {
                      searchKey: this.filters.location,
                      results: this.mobileProperties,
                      total: this.totalProperties,
                      type: 'location',
                      name: search?.name,
                    })
                    .subscribe({
                      next: () => {
                        this.eventService.broadcast('latest-searches-update');
                      },
                      error: (err: string) => {
                        if (err.startsWith('objectStore does not exists')) {
                          this.dbService.createObjectStore({
                            store: 'latest-searches',
                            storeConfig: {
                              keyPath: 'searchKey',
                              autoIncrement: false,
                            },
                            storeSchema: [],
                          });
                        }
                      },
                    });
                }
              });
          }
          this.eventService.broadcast('mobile-properties-result');
        },
        error: (err) => {
          if (this.lastSearch.isLastSearch) {
            this.dbService
              .getByKey('latest-searches', this.lastSearch.value)
              .subscribe((data: any) => {
                this.totalProperties = data.total;
                if (this.isMobile.value && this.mobileProperties?.length > 0) {
                  if (this.pushProperties) {
                    data.results.forEach((property: any) =>
                      this.mobileProperties.push(property)
                    );
                  } else {
                    this.pushProperties = true;
                    this.mobileProperties = data.properties;
                  }
                } else {
                  this.desktopProperties = data.results;
                  this.mobileProperties = data.results;
                }
              });
          } else {
            if (filters.location) {
              let location = filters.location.split(':');
              if (location.length <= 4) {
                location = Number(location[2].replace(/[^0-9]/g, ''));
                if (location) {
                  this.desktopProperties = [];
                  this.mobileProperties = [];
                  this.dbService
                    .getAll('searched-properties')
                    .subscribe((properties: any[]) => {
                      properties.forEach((prop) => {
                        let propLocation =
                          prop?.similarKeys?.location?.split(':');
                        if (propLocation?.length > 3) {
                          propLocation = Number(
                            propLocation[2].replace(/[^0-9]/g, '')
                          );
                          if (propLocation) {
                            if (propLocation === location) {
                              this.desktopProperties.push(prop);
                              this.mobileProperties.push(prop);
                            }
                          }
                        }
                      });
                      this.totalProperties = this.mobileProperties.length;
                    });
                }
              } else {
                location = Number(location[3].replace(/[^0-9]/g, ''));
                if (location) {
                  this.dbService
                    .getAll('searched-properties')
                    .subscribe((properties: any[]) => {
                      properties.forEach((prop) => {
                        let propLocation =
                          prop?.similarKeys?.location?.split(':');
                        if (propLocation?.length > 4) {
                          propLocation = Number(
                            propLocation[3].replace(/[^0-9]/g, '')
                          );
                          if (propLocation) {
                            if (propLocation === location) {
                              this.desktopProperties.push(prop);
                              this.mobileProperties.push(prop);
                            }
                          }
                        }
                      });
                      this.totalProperties = this.mobileProperties.length;
                    });
                }
              }
            } else {
              this.dbService
                .getAll('searched-properties')
                .subscribe((properties: any[]) => {
                  this.desktopProperties = properties;
                  this.mobileProperties = properties;
                  this.totalProperties = properties.length;
                });
            }
          }
        },
      });
    this.subscriptions.add(propertySubscription);
  }

  degreesToRadians(degrees: number) {
    return (degrees * Math.PI) / 180;
  }

  calcNearbyProperties() {
    if (this.mobileProperties?.length > 0 && this.isNearby) {
      navigator.geolocation.getCurrentPosition(position => {
        const newLatLng = { lat: position.coords.latitude, lng: position.coords.longitude };
        localStorage.setItem('latlng', JSON.stringify(newLatLng));
      }, err => {
        if (err.code === err.PERMISSION_DENIED) {

        }
        localStorage.removeItem('latlng');
      });
      if (localStorage.getItem('latlng')) {
        let userLatLng: any = localStorage.getItem('latlng');
        userLatLng = JSON.parse(userLatLng);

        this.mobileProperties.forEach((prop) => {
          if (prop.latitude && prop.longitude) {
            var latA = this.degreesToRadians(prop.latitude);
            var lonA = this.degreesToRadians(prop.longitude);
            var latB = this.degreesToRadians(userLatLng.lat);
            var lonB = this.degreesToRadians(userLatLng.lng);
            var R = 6371000;
            var latC = latB - latA;
            var lonC = lonB - lonA;
            var A =
              Math.sin(latC / 2) * Math.sin(latC / 2) +
              Math.sin(lonC / 2) *
              Math.sin(lonC / 2) *
              Math.cos(latA) *
              Math.cos(latB);
            var C = 2 * Math.asin(Math.sqrt(A));
            prop['distance'] = Math.round(R * C);
          } else {
            prop['distance'] = undefined;
          }
        });
        this.mobileProperties.sort((a, b) => {
          if (!a.distance && !b.distance) {
            return 0;
          }
          if (!a.distance) {
            return 1;
          }
          if (!b.distance) {
            return -1;
          }
          return a.distance - b.distance
        });
      }
    }
  }

  getDataToSearch() {
    const dataSubscription = this.propertyService
      .getDataToSearch()
      .subscribe((data) => {
        this.searchData = data;
      });
    this.subscriptions.add(dataSubscription);
  }

  getFeaturedProperties() {
    const highlightSubscription = this.propertyService
      .getHighlightsProperties()
      .subscribe({
        next: (data) => (this.highlightsProperties = data),
        error: () =>
          this.dbService
            .getAll('highlights-properties')
            .subscribe(
              (properties) => (this.highlightsProperties = properties)
            ),
      });
    this.subscriptions.add(highlightSubscription);
  }

  onResize(event: any = null) {
    if (event === null) {
      window.innerWidth > 1024
        ? this.isMobile.next(false)
        : this.isMobile.next(true);
    } else {
      event.outerWidth < 1024 || event.screen.orientation.angle > 0
        ? this.isMobile.next(true)
        : this.isMobile.next(false);
    }
  }

  loadCss() {
    const dynamicCss = ['assets/css/lightcase.css', 'assets/css/slick.css'];

    for (let i = 0; i < dynamicCss.length; i++) {
      const node = document.createElement('link');
      node.rel = 'stylesheet';
      node.href = dynamicCss[i];
      document.getElementsByTagName('head')[0].appendChild(node);
    }
  }

  ngOnDestroy(): void {
    this.resizeSubscription$.unsubscribe();
    this.subscriptions.unsubscribe();
  }
}
